// src/services/api.ts
const API_URL = '';

export const INSTITUTIONAL_EMAIL = 'ppp.educacaoinfantil@portovelho.ro.gov.br';

// ==================== TIPOS ====================

export interface Stage {
  id: string;
  stage: number;
  title: string;
  description: string;
  status: string;
  duration: string;
}

export interface DocumentData {
  id: string;
  title: string;
  description: string | null;
  filename: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  category: string;
  isPublished: boolean;
  order: number;
  createdAt: string;
}

export interface UploadDocumentData {
  title: string;
  description?: string;
  category?: string;
  order?: number;
  file: File;
}

export interface UpdateDocumentData {
  title?: string;
  description?: string;
  category?: string;
  order?: number;
  isPublished?: boolean;
}

export interface Report {
  id: string;
  title: string;
  description: string | null;
  filename: string;
  originalName: string;
  fileSize: number;
  mimeType: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UploadReportData {
  title: string;
  description?: string;
  file: File;
}

// ==================== TOKEN ====================

/**
 * Dispara evento global de logout
 */
export function triggerLogoutEvent(): void {
  window.dispatchEvent(new CustomEvent('auth:logout'));
  console.log('[TOKEN] Evento de logout disparado');
}

/**
 * Verifica se o token é válido (não expirado)
 * Com margem de segurança de 5 minutos
 */
export function isTokenValid(token: string): boolean {
  if (!token || token.trim() === '') return false;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    const payload = JSON.parse(atob(parts[1]));
    const expDate = new Date(payload.exp * 1000);
    const now = new Date();
    
    // Margem de 5 minutos para evitar expiração durante requisições
    const marginMs = 5 * 60 * 1000;
    const isValid = expDate > new Date(now.getTime() + marginMs);
    
    if (!isValid) {
      console.warn('[TOKEN] Token expirado em:', expDate.toLocaleString());
      console.warn('[TOKEN] Token:', token.substring(0, 30) + '...');
    }
    
    return isValid;
  } catch (error) {
    console.error('[TOKEN] Erro ao validar token:', error);
    return false;
  }
}

/**
 * Obtém token do localStorage (admin_token)
 */
export function getTokenFromStorage(): string | null {
  const token = localStorage.getItem('admin_token');
  
  if (!token) {
    console.warn('[TOKEN] Nenhum token encontrado no localStorage');
  }
  
  return token;
}

/**
 * Salva token no localStorage
 */
export function saveTokenToStorage(token: string): void {
  localStorage.setItem('admin_token', token);
  console.log('[TOKEN] Token salvo no localStorage, primeiros 30 chars:', token.substring(0, 30) + '...');
}

/**
 * Remove token do localStorage
 */
export function clearTokenFromStorage(): void {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_data');
  console.log('[TOKEN] Token removido do storage');
}

/**
 * Função auxiliar para fazer requisições autenticadas
 * Verifica token, adiciona header Authorization e trata erros 401/403
 */
async function authFetch<T>(url: string, token: string, options: RequestInit = {}): Promise<T> {
  if (!token) {
    clearTokenFromStorage();
    triggerLogoutEvent();
    throw new Error('Token não encontrado. Faça login novamente.');
  }
  
  // Validar token localmente antes da requisição
  if (!isTokenValid(token)) {
    console.warn('[AUTH_FETCH] Token inválido ou expirado, limpando storage');
    clearTokenFromStorage();
    triggerLogoutEvent();
    throw new Error('Token expirado. Faça login novamente.');
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    });
    
    // Se receber 401 ou 403, token inválido no backend
    if (response.status === 401 || response.status === 403) {
      console.warn('[AUTH_FETCH] Token rejeitado pelo servidor (401/403)');
      clearTokenFromStorage();
      triggerLogoutEvent();
      throw new Error('Sessão expirada. Faça login novamente.');
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
      throw new Error(errorData.error || `Erro HTTP ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    // Se for erro de rede, repassar
    if (error instanceof Error) {
      // Se for erro relacionado a token, garantir logout
      if (error.message.includes('Token') || error.message.includes('Sessão')) {
        clearTokenFromStorage();
        triggerLogoutEvent();
      }
      throw error;
    }
    throw new Error('Erro na requisição');
  }
}

// ==================== API ====================

export const api = {
  // ========== STAGES (FASES DO PROJETO) ==========
  
  /**
   * Busca todas as fases do projeto (público)
   */
  async getAllStages(): Promise<Stage[]> {
    try {
      const response = await fetch(`${API_URL}/api/stages`);
      if (!response.ok) {
        throw new Error('Erro ao buscar fases');
      }
      return await response.json();
    } catch (error) {
      console.error('[STAGES] Erro ao buscar fases:', error);
      // Retorna dados mockados caso a API não esteja disponível
      return [
        { 
          id: '1', 
          stage: 1, 
          title: 'Planejamento, Diagnóstico e Estudos', 
          description: 'Levantamento de dados, estudos de viabilidade, diagnósticos e estruturação inicial do projeto. Inclui a Escuta Social e a coleta de contribuições da comunidade.', 
          status: 'em-andamento', 
          duration: '8 meses' 
        },
        { 
          id: '2', 
          stage: 2, 
          title: 'EVTEA e Estruturação do Contrato', 
          description: 'Elaboração do Estudo de Viabilidade Técnica, Econômica e Ambiental (EVTEA), modelagem econômico-financeira e minutas contratuais.', 
          status: 'a-iniciar', 
          duration: '5 a 6 meses' 
        },
        { 
          id: '3', 
          stage: 3, 
          title: 'Validação Externa', 
          description: 'Consulta Pública, Audiência Pública, roadshows com investidores e revisões dos órgãos de controle.', 
          status: 'a-iniciar', 
          duration: '5 a 6 meses' 
        },
        { 
          id: '4', 
          stage: 4, 
          title: 'Licitação e Contratação', 
          description: 'Publicação do edital, processo licitatório, seleção do parceiro privado e assinatura do contrato de concessão.', 
          status: 'a-iniciar', 
          duration: '6 a 7 meses' 
        }
      ];
    }
  },

  /**
   * Busca a fase atual do projeto (público)
   */
  async getCurrentStage(): Promise<{ currentStage: number }> {
    try {
      const response = await fetch(`${API_URL}/api/stages/current`);
      if (!response.ok) {
        throw new Error('Erro ao buscar fase atual');
      }
      return await response.json();
    } catch (error) {
      console.error('[CURRENT_STAGE] Erro:', error);
      return { currentStage: 1 };
    }
  },

  /**
   * Atualiza uma fase do projeto (requer autenticação)
   */
  async updateStage(
    token: string, 
    stageId: string, 
    data: { 
      status?: string; 
      title?: string; 
      description?: string; 
      duration?: string 
    }
  ): Promise<Stage> {
    return authFetch(`${API_URL}/api/stages/${stageId}`, token, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  /**
   * Cria uma nova fase (requer autenticação)
   */
  async createStage(
    token: string,
    data: {
      stage: number;
      title: string;
      description: string;
      status?: string;
      duration: string;
    }
  ): Promise<Stage> {
    return authFetch(`${API_URL}/api/stages`, token, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  /**
   * Remove uma fase (requer autenticação)
   */
  async deleteStage(token: string, stageId: string): Promise<{ success: boolean; message: string }> {
    return authFetch(`${API_URL}/api/stages/${stageId}`, token, {
      method: 'DELETE'
    });
  },

  // ========== ADMIN AUTH ==========
  
  /**
   * Realiza login do administrador
   */
  async login(email: string, password: string): Promise<{ token: string; admin: { id: string; email: string; name: string } }> {
    console.log('[LOGIN] Tentando login para:', email);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Erro no login' }));
        console.error('[LOGIN] Erro:', error);
        throw new Error(error.error || 'Credenciais inválidas');
      }
      
      const data = await response.json();
      console.log('[LOGIN] ✅ Login bem-sucedido para:', email);
      
      // Salvar token no localStorage
      saveTokenToStorage(data.token);
      
      return data;
    } catch (error) {
      console.error('[LOGIN] ❌ Falha no login:', error);
      throw error;
    }
  },

  /**
   * Realiza logout do administrador
   */
  async logout(token: string): Promise<void> {
    console.log('[LOGOUT] Realizando logout');
    
    try {
      if (token && isTokenValid(token)) {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        console.log('[LOGOUT] Logout realizado com sucesso no backend');
      }
    } catch (error) {
      console.error('[LOGOUT] Erro no logout da API:', error);
    } finally {
      // Sempre limpar o storage local, mesmo se o backend falhar
      clearTokenFromStorage();
    }
  },

  /**
   * Busca dados do administrador logado (valida token)
   */
  async getMe(token: string): Promise<{ id: string; email: string; name: string }> {
    console.log('[GET_ME] Buscando dados do admin');
    return authFetch(`${API_URL}/api/auth/me`, token);
  },

  // ========== ADMIN LOGS ==========
  
  /**
   * Busca logs de administração
   */
  async getAdminLogs(token: string, page: number = 1, limit: number = 50): Promise<{ logs: any[]; total: number; page: number; limit: number }> {
    return authFetch(`${API_URL}/api/admin/logs?page=${page}&limit=${limit}`, token);
  },

  // ========== ADMIN STATS ==========
  
  /**
   * Busca estatísticas do admin (requer autenticação)
   */
  async getAdminStats(token: string): Promise<{ 
    submissions: { social: number; ativa: number; contact: number }; 
    stages: any[] 
  }> {
    console.log('[STATS] Buscando estatísticas...');
    console.log('[STATS] Token (primeiros 20 chars):', token?.substring(0, 20) + '...');
    
    try {
      const result = await authFetch<{ 
        submissions: { social: number; ativa: number; contact: number }; 
        stages: any[] 
      }>(`${API_URL}/api/admin/stats`, token);
      
      console.log('[STATS] ✅ Estatísticas obtidas:', result);
      return result;
    } catch (error) {
      console.error('[STATS] ❌ Erro ao buscar estatísticas:', error);
      throw error;
    }
  },

  // ========== DOCUMENTOS ==========
  
  /**
   * Busca documentos públicos
   */
  async getDocuments(): Promise<DocumentData[]> {
    try {
      const response = await fetch(`${API_URL}/api/documents`);
      if (!response.ok) throw new Error('Erro ao buscar documentos');
      return response.json();
    } catch (error) {
      console.error('[DOCUMENTS] Erro ao buscar documentos:', error);
      return [];
    }
  },

  /**
   * Baixa um documento
   */
  async downloadDocument(documentId: string, fileName: string): Promise<void> {
    try {
      const response = await fetch(`${API_URL}/api/documents/${documentId}/download`);
      if (!response.ok) throw new Error('Erro ao baixar documento');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('[DOWNLOAD] Erro ao baixar documento:', error);
      throw error;
    }
  },

  /**
   * Busca todos os documentos (admin)
   */
  async getAdminDocuments(token: string): Promise<DocumentData[]> {
    return authFetch(`${API_URL}/api/admin/documents`, token);
  },

  /**
   * Faz upload de documento (admin)
   */
  async uploadDocument(token: string, data: UploadDocumentData): Promise<{ success: boolean; document: DocumentData }> {
    const formData = new FormData();
    formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);
    if (data.category) formData.append('category', data.category);
    if (data.order !== undefined) formData.append('order', String(data.order));
    formData.append('file', data.file);

    try {
      const response = await fetch(`${API_URL}/api/admin/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Erro ao fazer upload' }));
        throw new Error(error.error || 'Erro ao fazer upload');
      }

      return response.json();
    } catch (error) {
      console.error('[UPLOAD] Erro:', error);
      throw error;
    }
  },

  /**
   * Atualiza documento (admin)
   */
  async updateDocument(token: string, documentId: string, data: UpdateDocumentData): Promise<{ success: boolean; document: DocumentData }> {
    return authFetch(`${API_URL}/api/admin/documents/${documentId}`, token, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  /**
   * Remove documento (admin)
   */
  async deleteDocument(token: string, documentId: string): Promise<{ success: boolean }> {
    return authFetch(`${API_URL}/api/admin/documents/${documentId}`, token, {
      method: 'DELETE'
    });
  },

  // ========== RELATÓRIOS ==========

  /**
   * Busca o relatório publicado mais recente (público)
   */
  async getReport(): Promise<Report | null> {
    try {
      const response = await fetch(`${API_URL}/api/reports`);
      if (!response.ok) throw new Error('Erro ao buscar relatório');
      return response.json();
    } catch (error) {
      console.error('[REPORTS] Erro:', error);
      return null;
    }
  },

  /**
   * Busca todos os relatórios (admin)
   */
  async getAdminReports(token: string): Promise<Report[]> {
    return authFetch(`${API_URL}/api/admin/reports`, token);
  },

  /**
   * Baixa um relatório (público)
   */
  async downloadReport(reportId: string, fileName: string): Promise<void> {
    try {
      const response = await fetch(`${API_URL}/api/reports/${reportId}/download`);
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Erro ao baixar relatório' }));
        throw new Error(error.error || 'Erro ao baixar relatório');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('[REPORTS] Erro no download:', error);
      throw error;
    }
  },

  /**
   * Faz upload de relatório e o publica (admin)
   */
  async uploadReport(token: string, data: UploadReportData): Promise<{ success: boolean; report: Report }> {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);

    try {
      const response = await fetch(`${API_URL}/api/admin/reports`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Erro ao fazer upload' }));
        throw new Error(error.error || 'Erro ao fazer upload');
      }

      return response.json();
    } catch (error) {
      console.error('[REPORTS] Erro no upload:', error);
      throw error;
    }
  },

  /**
   * Atualiza título/descrição/status de publicação de um relatório (admin)
   */
  async updateReport(token: string, id: string, data: Partial<Report>): Promise<{ success: boolean; report: Report }> {
    return authFetch(`${API_URL}/api/admin/reports/${id}`, token, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  /**
   * Remove um relatório (admin)
   */
  async deleteReport(token: string, id: string): Promise<{ success: boolean }> {
    return authFetch(`${API_URL}/api/admin/reports/${id}`, token, {
      method: 'DELETE'
    });
  }
};